import { useCallback, useEffect, useRef } from "react";
import useProfiler from "../../../hooks/useProfiler.js";
import { createTrackingUtils } from "../index.js";
import { flightsLocalStore } from "@flights/web-api-utils-universal";
import { REFRESH_RESULTS_TIMEOUT } from "../../../constants.js";
import useEventTracking from "../../../hooks/useEventTracking.js";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
const utils = createTrackingUtils({
    name: "flights_funnel_fake_staggered_search_web",
    defaultStage: 1,
    stages: {
        main: 1,
        desktop: 2,
        mobile: 3
    },
    goals: {}
});
export const useTrackFakeStaggeredSearch = () => {
    const { isDesktop, isMobile } = useProfiler();
    return useCallback(() => {
        const variant = utils.trackWithDefaultStage();
        isDesktop() && utils.stages.desktop();
        isMobile() && utils.stages.mobile();
        return variant;
    }, [isDesktop, isMobile]);
};
const staggeredSearchStoreKey = "staggered_search";
const staggeredSearchStore = {
    getParamsString: (searchParams) => {
        const type = searchParams.get("type");
        const cabinClass = searchParams.get("cabinClass");
        const from = searchParams.get("from");
        const to = searchParams.get("to");
        const adults = searchParams.get("adults");
        const children = searchParams.get("children")?.split(",").sort().join(",");
        return [type, cabinClass, from, to, adults, children].join("/");
    },
    getSearch: (searchParams) => {
        const searchString = staggeredSearchStore.getParamsString(searchParams);
        const storedSearches = flightsLocalStore.get(staggeredSearchStoreKey) ?? {};
        const foundSearch = storedSearches[searchString];
        if (Date.now() - foundSearch < REFRESH_RESULTS_TIMEOUT) {
            return true;
        }
        delete storedSearches[searchString];
        flightsLocalStore.set(staggeredSearchStoreKey, storedSearches);
        return false;
    },
    addSearch: (searchParams) => {
        const searchString = staggeredSearchStore.getParamsString(searchParams);
        const storedSearches = flightsLocalStore.get(staggeredSearchStoreKey) ?? {};
        storedSearches[searchString] = Date.now();
        flightsLocalStore.set(staggeredSearchStoreKey, storedSearches);
    }
};
const getRandomLatency = (type) => {
    if (type !== "ONEWAY" && type !== "ROUNDTRIP") {
        return 0;
    }
    const min = 1;
    const max = type === "ONEWAY" ? 6000 : 14000;
    return Math.floor(Math.random() * (max - min + 1)) + min;
};
const getRandomResultsSize = () => {
    const rng = Math.random();
    if (rng < 0.25)
        return 2;
    if (rng < 0.5)
        return 3;
    if (rng < 0.75)
        return 4;
    return 5;
};
const getRandomResultsIndexes = () => {
    const searchResultsPageSize = 15;
    const fakeResultsSize = getRandomResultsSize();
    const picks = new Set();
    while (picks.size < fakeResultsSize) {
        picks.add(Math.floor(Math.random() * searchResultsPageSize));
    }
    return Array.from(picks);
};
const getSearchStaggeringData = (searchParams, isCached) => {
    const type = searchParams.get("type");
    const caSource = searchParams.get("ca_source");
    let hasRequirements = true;
    if (type === "MULTISTOP") {
        hasRequirements = false;
    }
    if (!isCached) {
        hasRequirements = false;
    }
    if (!caSource) {
        hasRequirements = false;
    }
    const latency = getRandomLatency(type);
    if (latency === 0) {
        hasRequirements = false;
    }
    if (staggeredSearchStore.getSearch(searchParams)) {
        hasRequirements = false;
    }
    staggeredSearchStore.addSearch(searchParams);
    return {
        hasRequirements,
        latency
    };
};
export const useGetStaggeredSearchResults = () => {
    const trackFakeStaggeredSearch = useTrackFakeStaggeredSearch();
    const { requestId } = useGlobalContext();
    const trackV2 = useEventTracking("search_results", requestId);
    return useCallback((searchParams, isCached) => {
        const { hasRequirements, latency } = getSearchStaggeringData(searchParams, isCached);
        const variant = hasRequirements ? trackFakeStaggeredSearch() : 0;
        trackV2("assigned_fake_latency_ms", hasRequirements ? latency : 0);
        let visibleIndexes = undefined;
        if (variant === 3) {
            visibleIndexes = getRandomResultsIndexes();
        }
        return { variant, latency, visibleIndexes };
    }, [trackFakeStaggeredSearch, trackV2]);
};
export const useDelayResponse = () => {
    const timeoutRef = useRef(null);
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    return useCallback((callback, ms) => {
        timeoutRef.current = setTimeout(callback, ms);
    }, []);
};
export default utils;
